import { useSelector } from "react-redux";
import { useState, useCallback, useEffect, useMemo } from "react";
import { Warehouse } from "$types";
import GenericSelect, { GenericSelectProps } from "./GenericSelect";
import { appState } from "../reducers";
import { EntrepotService } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

type Props = {
  onChange: (contactId, contact) => void;
  value: any;
  valueKey?: string;
  error?: boolean;
  multiple?: boolean;
  required?: boolean;
  disabled?: boolean;
  companyId?: string;
  intervenantCompanyId?: string;
  label?: string;
  placeholder?: string;
  noPlaceHolder?: boolean;
  className?: string;
  allowNoCompanyFilter?: boolean;
  allowAloneWarehouse?: boolean;
  warehouseOptions?: Warehouse[];
};
function WarehouseSelect({
  disabled,
  className,
  companyId,
  intervenantCompanyId,
  warehouseOptions,
  valueKey = "_id",
  label = "Entrepôt",
  noPlaceHolder = false,
  placeholder = "Sélectionnez un entrepôt",
  allowNoCompanyFilter = false,
  allowAloneWarehouse = false,
  ...props
}: Props & Partial<GenericSelectProps>) {
  const { filter: sidebarCompanyFilter } = useSelector(appState);
  const [warehouses, setWarehouses] = useState([]);
  const [entrepotsOptions, setEntrepotsOptions] = useState([]);

  const companyFilterIds = useMemo(() => {
    const result = [];
    if (sidebarCompanyFilter?._id) {
      result.push(sidebarCompanyFilter._id);
    } else if (companyId) {
      result.push(companyId);
    }
    // TODO : voir avec les intervening UNIQUEMENT du chantier
    if (intervenantCompanyId) {
      result.push(intervenantCompanyId);
    }
    return result;
  }, [sidebarCompanyFilter, companyId, intervenantCompanyId]);

  const fecthDockyards = useCallback(async () => {
    if (!warehouseOptions) {
      const filters = {
        ...(companyFilterIds && { $inCompany: companyFilterIds }),
      };
      const queryKey = [CacheInvalidationAction.GET_ENTREPOTS, filters];
      let getWarehouses: any = await queryClient.getQueryData(queryKey);
      if (!getWarehouses) {
        getWarehouses = await queryClient.fetchQuery(
          queryKey,
          () => EntrepotService.getEntrepots(filters),
          { cacheTime: 60 * 1000 }
        );
        queryClient.setQueryData(queryKey, getWarehouses);
      }
      setWarehouses(getWarehouses?.data || []);
    }
  }, [companyFilterIds, warehouseOptions]);

  useEffect(() => {
    fecthDockyards();
  }, [fecthDockyards]);

  const fecthWarehouseOptions = useCallback(async () => {
    setEntrepotsOptions(
      warehouseOptions ||
        (companyFilterIds.length
          ? (warehouses || []).filter((e: Warehouse) => {
              if (e.companies.length === 0 && allowAloneWarehouse) {
                return true;
              }
              return (
                e.companies
                  .map((c) => c._id)
                  .some((cId) => companyFilterIds.includes(cId)) ||
                companyFilterIds.includes(e?.company?._id)
              );
            })
          : allowNoCompanyFilter
          ? warehouses || []
          : []
        ).sort((e1, e2) => e1.name.localeCompare(e2.name))
    );
  }, [
    warehouseOptions,
    companyFilterIds,
    warehouses,
    allowNoCompanyFilter,
    allowAloneWarehouse,
  ]);

  useEffect(() => {
    fecthWarehouseOptions();
  }, [fecthWarehouseOptions]);

  let placeholderText = noPlaceHolder
    ? ""
    : companyId || intervenantCompanyId || allowNoCompanyFilter
    ? placeholder
    : "Vous devez d'abord sélectionner une entreprise";

  if (companyFilterIds.length > 0 && entrepotsOptions.length === 0) {
    placeholderText = "Aucun contact pour cette entreprise";
  }

  return (
    <GenericSelect
      valueKey={valueKey}
      label={label}
      className={className}
      placeholder={placeholderText}
      disabled={disabled || entrepotsOptions.length === 0}
      options={entrepotsOptions}
      getOptionLabel={(option: any) => `${option.name}`}
      {...props}
    />
  );
}

export default WarehouseSelect;
